import React, {Fragment, useEffect, useRef, useState} from "react";
import {
  Alert,
  Button,
  Card,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  InputGroup,
  Progress,
  Row,
  Table,
} from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import PaginationWrapper from "../components/pagination";
import {paginate} from "../../../utils";
import useHistoryState from "../../../common/useHistoryState";
import classnames from "classnames";
import ReactDatetime from "react-datetime";
import moment from "moment";
import pushService from "../../../services/push.service";

const extractHtmlText = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  if (!html.includes('<')) {
    return html.trim();
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;

  const title = wrapper.querySelector('title')?.textContent?.trim();
  const heading = wrapper.querySelector('h1')?.textContent?.trim();
  const body = wrapper.textContent?.replace(/\s+/g, ' ').trim();

  return [title, heading, body]
    .filter((value, index, array) => value && array.indexOf(value) === index)
    .join(' - ')
    .slice(0, 300);
};

const getErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;

  if (responseData?.resultMsg) {
    return responseData.resultMsg;
  }

  if (typeof responseData === 'string') {
    return extractHtmlText(responseData) || fallbackMessage;
  }

  return error?.message || fallbackMessage;
};

function Push() {
  const [pageActive, setPageActive] = useHistoryState(1, 'pageActive');
  const [pageCount, setPageCount] = useHistoryState(1, 'pageCount');
  const [historyRows, setHistoryRows] = useHistoryState([], 'historyRows');
  const [startDate, setStartDate] = useHistoryState(moment().add(-1, 'month').format('YYYY-MM-DD'), 'startDate');
  const [endDate, setEndDate] = useHistoryState(moment().format('YYYY-MM-DD'), 'endDate');
  const [searchCurrent, setSearchCurrent] = useHistoryState(false, 'searchCurrent');
  const [message, setMessage] = useHistoryState('', 'message');
  const [sendMode, setSendMode] = useHistoryState('all', 'sendMode');
  const [userSearch, setUserSearch] = useHistoryState('', 'userSearch');
  const [userSearchCurrent, setUserSearchCurrent] = useHistoryState(false, 'userSearchCurrent');
  const [userRows, setUserRows] = useHistoryState([], 'userRows');
  const [selectedUser, setSelectedUser] = useHistoryState(null, 'selectedUser');
  const [sendStatus, setSendStatus] = useHistoryState('', 'sendStatus');
  const [sendStatusType, setSendStatusType] = useHistoryState('', 'sendStatusType');
  const [sendProgress, setSendProgress] = useState({
    active: false,
    jobId: '',
    status: 'idle',
    totalCount: 0,
    processedCount: 0,
    successCount: 0,
    failedCount: 0,
    resultMsg: ''
  });
  const sendStatusTimerRef = useRef(null);

  const clearSendStatusTimer = () => {
    if (sendStatusTimerRef.current) {
      clearTimeout(sendStatusTimerRef.current);
      sendStatusTimerRef.current = null;
    }
  };

  const resetSendProgress = () => {
    clearSendStatusTimer();
    setSendProgress({
      active: false,
      jobId: '',
      status: 'idle',
      totalCount: 0,
      processedCount: 0,
      successCount: 0,
      failedCount: 0,
      resultMsg: ''
    });
  };

  const handleReactDatetimeChange = (who, date) => {
    if (
      startDate &&
      who === "endDate" &&
      new Date(startDate) > new Date(date.format('YYYY-MM-DD'))
    ) {
      setStartDate(date.format('YYYY-MM-DD'));
      setEndDate(date.format('YYYY-MM-DD'));
    } else if (
      endDate &&
      who === "startDate" &&
      new Date(endDate) < new Date(date.format('YYYY-MM-DD'))
    ) {
      setStartDate(date.format('YYYY-MM-DD'));
      setEndDate(date.format('YYYY-MM-DD'));
    } else if (who === "startDate") {
      setStartDate(date.format('YYYY-MM-DD'));
    } else {
      setEndDate(date.format('YYYY-MM-DD'));
    }
  };

  const renderHistory = () => {
    pushService.list({ startDate, endDate })
      .then((res) => res.data)
      .then((res) => {
        if (res.code === 200) {
          setPageCount(Math.max(res.list.length, 1));
          setHistoryRows(paginate(res.list, pageActive, 10));
        }
      })
      .catch(() => {
        alert('전송내역을 불러오지 못했습니다.');
      });
  };

  const searchUsers = () => {
    pushService.users({ search: userSearch.trim() })
      .then((res) => res.data)
      .then((res) => {
        if (res.code === 200) {
          setUserRows(res.list || []);
          if (selectedUser && !res.list?.some((item) => item.id === selectedUser.id)) {
            setSelectedUser(null);
          }
        } else {
          setUserRows([]);
          alert(res.resultMsg || '유저를 불러오지 못했습니다.');
        }
      })
      .catch(() => {
        setUserRows([]);
        alert('유저를 불러오지 못했습니다.');
      });
  };

  useEffect(() => {
    renderHistory();
  }, [pageActive, startDate, endDate]);

  useEffect(() => {
    if (sendMode === 'one' && userRows.length === 0) {
      searchUsers();
    }
    if (sendMode === 'all') {
      setSelectedUser(null);
    }
  }, [sendMode]);

  useEffect(() => () => {
    clearSendStatusTimer();
  }, []);

  const pollSendStatus = (jobId) => {
    pushService.sendStatus({ jobId })
      .then((res) => res.data)
      .then((res) => {
        if (res.code !== 200 || !res.job) {
          throw new Error(res.resultMsg || '전송 상태를 확인하지 못했습니다.');
        }

        const nextProgress = {
          active: res.job.status === 'queued' || res.job.status === 'running',
          jobId: res.job.jobId,
          status: res.job.status,
          totalCount: res.job.totalCount || 0,
          processedCount: res.job.processedCount || 0,
          successCount: res.job.successCount || 0,
          failedCount: res.job.failedCount || 0,
          resultMsg: res.job.resultMsg || ''
        };

        setSendProgress(nextProgress);

        if (res.job.status === 'completed') {
          setSendStatusType('success');
          setSendStatus(res.job.resultMsg || `${res.job.successCount || 0} 명에게 전송했습니다.`);
          setMessage('');
          renderHistory();
          clearSendStatusTimer();
          return;
        }

        if (res.job.status === 'failed') {
          setSendStatusType('danger');
          setSendStatus(res.job.resultMsg || '푸시 전송에 실패했습니다.');
          clearSendStatusTimer();
          return;
        }

        clearSendStatusTimer();
        sendStatusTimerRef.current = setTimeout(() => {
          pollSendStatus(jobId);
        }, 800);
      })
      .catch((e) => {
        resetSendProgress();
        setSendStatusType('danger');
        setSendStatus(getErrorMessage(e, '전송 진행 상태를 확인하지 못했습니다.'));
      });
  };

  const send = () => {
    if (!message || !message.trim()) {
      setSendStatusType('danger');
      setSendStatus('내용을 입력해주세요.');
      return;
    }

    if (sendMode === 'one' && !selectedUser) {
      setSendStatusType('danger');
      setSendStatus('대상 유저를 선택해주세요.');
      return;
    }

    setSendStatus('');
    setSendStatusType('');
    resetSendProgress();

    pushService.send({
      message: message.trim(),
      userId: sendMode === 'one' ? selectedUser.id : undefined,
    })
      .then(res => res.data)
      .then(res => {
        if (res.code === 202 && res.jobId) {
          setSendStatusType('info');
          setSendStatus(res.resultMsg || '푸시 전송을 시작했습니다.');
          setSendProgress({
            active: true,
            jobId: res.jobId,
            status: 'queued',
            totalCount: 0,
            processedCount: 0,
            successCount: 0,
            failedCount: 0,
            resultMsg: res.resultMsg || '푸시 전송을 시작했습니다.'
          });
          pollSendStatus(res.jobId);
        } else if (res.code === 200) {
          const successCount = typeof res.successCount === 'number' ? res.successCount : 0;
          setSendStatusType('success');
          setSendStatus(`${successCount} 명에게 전송했습니다.`);
          setMessage('');
          renderHistory();
        } else {
          setSendStatusType('danger');
          setSendStatus(res.resultMsg || '푸시 전송에 실패했습니다.');
        }
      })
      .catch((e) => {
        setSendStatusType('danger');
        setSendStatus(getErrorMessage(e, '푸시 전송에 실패했습니다.'));
      });
  };

  return (
    <>
      <SimpleHeader name="Tables" parentName="Tables" />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader className="border-0">
                <Row>
                  <Col xs="6">
                    <h3 className="mb-0">푸시 알림 전송</h3>
                  </Col>
                </Row>
                <Row className="py-3">
                  <Col xs="12">
                    <Form onSubmit={(e) => {
                      e.preventDefault();
                      send();
                    }}>
                      <Row className="mb-3">
                        <Col xs="12" md="6">
                          <div className="d-flex align-items-center">
                            <label className="mr-3 mb-0">
                              <input
                                type="radio"
                                name="sendMode"
                                checked={sendMode === 'all'}
                                onChange={() => setSendMode('all')}
                              />
                              <span className="ml-2">전체 전송</span>
                            </label>
                            <label className="mb-0">
                              <input
                                type="radio"
                                name="sendMode"
                                checked={sendMode === 'one'}
                                onChange={() => setSendMode('one')}
                              />
                              <span className="ml-2">유저 선택 전송</span>
                            </label>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs="12" md="6">
                          <FormGroup>
                            <InputGroup
                              className={classnames("input-group-merge", {
                                focused: searchCurrent,
                              })}
                            >
                              <Input
                                placeholder="내용을 입력해주세요."
                                type="text"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onFocus={() => setSearchCurrent(true)}
                                onBlur={() => setSearchCurrent(false)}
                              />
                            </InputGroup>
                          </FormGroup>
                        </Col>
                        <Col xs="12" md="2">
                          <Button color="primary" type="button" onClick={send} disabled={sendProgress.active}>
                            {sendProgress.active ? '전송 중...' : '보내기'}
                          </Button>
                        </Col>
                      </Row>
                      {sendProgress.jobId && (
                        <Row className="mb-3">
                          <Col xs="12" md="8">
                            <div className="d-flex justify-content-between text-sm mb-1">
                              <span>{sendProgress.resultMsg || '전송 준비 중입니다.'}</span>
                              <span>
                                {sendProgress.totalCount > 0
                                  ? `${sendProgress.processedCount}/${sendProgress.totalCount}`
                                  : '대상 조회 중'}
                              </span>
                            </div>
                            <Progress
                              animated={sendProgress.active}
                              color={sendProgress.status === 'failed' ? 'danger' : 'info'}
                              max={sendProgress.totalCount || 100}
                              value={sendProgress.totalCount > 0 ? sendProgress.processedCount : 100}
                            />
                            <div className="text-sm text-muted mt-2">
                              성공 {sendProgress.successCount}명 / 실패 {sendProgress.failedCount}명
                            </div>
                          </Col>
                        </Row>
                      )}
                      {sendStatus && (
                        <Row>
                          <Col xs="12" md="8">
                            <Alert className="mb-0" color={sendStatusType || 'info'}>
                              {sendStatus}
                            </Alert>
                          </Col>
                        </Row>
                      )}
                    </Form>
                  </Col>
                </Row>

                {sendMode === 'one' && (
                  <>
                    <Row className="py-2">
                      <Col xs="12" md="6">
                        <Form onSubmit={(e) => {
                          e.preventDefault();
                          searchUsers();
                        }}>
                          <FormGroup>
                            <InputGroup
                              className={classnames("input-group-merge", {
                                focused: userSearchCurrent,
                              })}
                            >
                              <Input
                                placeholder="아이디, 이름, 직번, 전화번호로 검색"
                                type="text"
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                onFocus={() => setUserSearchCurrent(true)}
                                onBlur={() => setUserSearchCurrent(false)}
                              />
                            </InputGroup>
                          </FormGroup>
                        </Form>
                      </Col>
                      <Col xs="12" md="2">
                        <Button color="default" type="button" onClick={searchUsers}>
                          유저 검색
                        </Button>
                      </Col>
                      <Col xs="12" className="mt-2">
                        {selectedUser ? (
                          <div className="text-sm">
                            선택된 유저: {selectedUser.loginId} ({selectedUser.name})
                          </div>
                        ) : (
                          <div className="text-sm text-muted">
                            전송할 유저를 검색해서 선택하세요.
                          </div>
                        )}
                      </Col>
                    </Row>
                    <Table className="align-items-center table-flush mt-3" responsive striped>
                      <thead className="thead-light">
                      <tr>
                        <th className="text-center" style={{ width: '10%' }}>선택</th>
                        <th className="text-center" style={{ width: '15%' }}>유저ID</th>
                        <th className="text-center" style={{ width: '25%' }}>아이디</th>
                        <th className="text-center" style={{ width: '20%' }}>이름</th>
                        <th className="text-center" style={{ width: '15%' }}>타입</th>
                        <th className="text-center" style={{ width: '15%' }}>수정일</th>
                      </tr>
                      </thead>
                      <tbody>
                      {userRows.map((item) => (
                        <tr key={item.id} className={selectedUser?.id === item.id ? 'table-active' : ''}>
                          <td className="text-center">
                            <Button
                              color={selectedUser?.id === item.id ? 'success' : 'default'}
                              size="sm"
                              type="button"
                              onClick={() => setSelectedUser(item)}
                            >
                              {selectedUser?.id === item.id ? '선택됨' : '선택'}
                            </Button>
                          </td>
                          <td className="text-center">{item.id}</td>
                          <td className="text-center">{item.loginId}</td>
                          <td className="text-center">{item.name}</td>
                          <td className="text-center">{item.type}</td>
                          <td className="text-center">{item.updatedAt ? moment(item.updatedAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                        </tr>
                      ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </CardHeader>
            </Card>
          </div>
        </Row>

        <Row className="mt-4">
          <div className="col">
            <Card>
              <CardHeader className="border-0">
                <Row>
                  <Col xs="6">
                    <h3 className="mb-0">푸시 알림 전송내역</h3>
                  </Col>
                </Row>
                <Row className="py-3">
                  <Col xs="12" md="2">
                    <ReactDatetime
                      inputProps={{ placeholder: "" }}
                      value={startDate}
                      locale='ko'
                      timeFormat={false}
                      dateFormat={'YYYY-MM-DD'}
                      onChange={(e) => handleReactDatetimeChange("startDate", e)}
                    />
                  </Col>
                  <Col xs="12" md="2">
                    <ReactDatetime
                      inputProps={{ placeholder: "" }}
                      value={endDate}
                      locale='ko'
                      timeFormat={false}
                      dateFormat={'YYYY-MM-DD'}
                      onChange={(e) => handleReactDatetimeChange("endDate", e)}
                    />
                  </Col>
                </Row>
              </CardHeader>

              <Table className="align-items-center table-flush" responsive striped>
                <thead className="thead-light">
                <tr>
                  <th className="text-center" style={{ width: '10%' }}>번호</th>
                  <th className="text-center" style={{ width: '15%' }}>전송자</th>
                  <th className="text-center" style={{ width: '15%' }}>전송날짜</th>
                  <th className="text-center" style={{ width: '60%' }}>내용</th>
                </tr>
                </thead>
                <tbody>
                {historyRows?.map((item, i) => (
                  <Fragment key={i}>
                    <tr>
                      <td className="text-center">{item.id}</td>
                      <td className="text-center">{item.userLoginId || item.userName || item.userId}</td>
                      <td className="text-center">{item.createdAt}</td>
                      <td className="text-center">{item.message}</td>
                    </tr>
                  </Fragment>
                ))}
                </tbody>
              </Table>

              <CardFooter className="py-4">
                <PaginationWrapper
                  isActive={pageActive}
                  pageNumber={pageCount}
                  callback={(page) => setPageActive(page)}
                  itemPerPage={10}
                />
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
}

export default Push;
